import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAppContext } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  X,
  Plus,
  Minus,
  Trash2,
  Loader2,
  Check,
  Download,
  Printer,
  Maximize2,
  CheckSquare,
  XSquare,
  Info,
  MapPin,
  Search,
  AlertTriangle,
  ChevronDown,
  Package,
  Save,
  FileText,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import ServiceBadge from "../components/ServiceBadge";

// Place Search Dropdown Component (like First Freight portal)
const PlaceSearchDropdown = ({ value, onPlaceSelect, placeholder = "Search SA suburb or postal code (e.g. 7405)...", dropdownPosition = "right", postalCode, cityHint }) => {
  const [inputValue, setInputValue] = useState(value?.town || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasAutoVerifiedRef = useRef(false);

  // Sync inputValue with prop value — only when place is actually verified
  useEffect(() => {
    if (value?.place && value?.town && value.town !== inputValue) {
      setInputValue(value.town);
    }
  }, [value?.town, value?.place]);

  // Auto-verify disabled — field must be blank until user types
  // The verify field is intentionally manual for accuracy

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Perform search with proper cancellation - searches by name AND postal code
  const performSearch = async (query, autoSelect = false) => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      let results = [];
      
      // Use unified verify endpoint — handles postal codes, names, cities, towns
      const response = await axios.get(`${API}/ff/places/verify/${encodeURIComponent(query.trim())}`, {
        signal: abortControllerRef.current.signal,
        timeout: 15000
      });
      if (response.data?.success && response.data?.places?.length > 0) {
        results = response.data.places;
      }
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setSearching(false);
        if (results.length > 0) {
          // Filter out unwanted results (Boxes, Busse - PO boxes and bus stations)
          const excludeTerms = ['boxes', 'busse', 'box ', 'p.o.', 'p o box', 'posbus'];
          const filteredResults = results.filter(place => {
            const townLower = (place.town || '').toLowerCase();
            return !excludeTerms.some(term => townLower.includes(term));
          });
          
          // Sort results: exact matches first, then starts-with, then contains
          const queryLower = query.toLowerCase().trim();
          filteredResults.sort((a, b) => {
            const aLower = (a.town || '').toLowerCase();
            const bLower = (b.town || '').toLowerCase();
            if (aLower === queryLower && bLower !== queryLower) return -1;
            if (bLower === queryLower && aLower !== queryLower) return 1;
            const aStarts = aLower.startsWith(queryLower);
            const bStarts = bLower.startsWith(queryLower);
            if (aStarts && !bStarts) return -1;
            if (bStarts && !aStarts) return 1;
            return aLower.localeCompare(bLower);
          });
          
          if (filteredResults.length > 0) {
            // Auto-select: if 1 result, or cityHint matches one
            if (autoSelect) {
              let bestMatch = null;
              if (filteredResults.length === 1) {
                bestMatch = filteredResults[0];
              } else if (cityHint) {
                const hint = cityHint.toUpperCase();
                bestMatch = filteredResults.find(p => {
                  const town = (p.town || '').toUpperCase();
                  return town.includes(hint) || hint.includes(town.split(',')[0]);
                });
              }
              if (bestMatch) {
                setInputValue(bestMatch.town);
                setSuggestions([]);
                setShowDropdown(false);
                setNoResults(false);
                onPlaceSelect?.(bestMatch);
                return;
              }
            }
            
            setSuggestions(filteredResults.slice(0, 15));
            setShowDropdown(true);
            setNoResults(false);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
            setNoResults(true);
          }
        } else {
          setSuggestions([]);
          setShowDropdown(false);
          setNoResults(true);
        }
      }
    } catch (err) {
      // Ignore abort errors (they're expected when we cancel)
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Place search error:', err);
      }
      if (isMountedRef.current) {
        setSearching(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setNoResults(false);  // Reset no results when user types

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (query.length >= 2) {
      setSearching(true);
      // Debounce the search by 400ms
      timeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 400);
    } else {
      setSearching(false);
      setSuggestions([]);
      setShowDropdown(false);
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  };

  const handleSelect = (place) => {
    // Clear search state
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setInputValue(place.town);
    setSearching(false);
    setSuggestions([]);
    setShowDropdown(false);
    setNoResults(false);
    
    // Call parent callback with full place object
    onPlaceSelect?.(place);
  };

  const isValid = value?.place; // Has a valid place ID from First Freight

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {/* Only show dropdown when user types, not on click */}}
          placeholder={placeholder}
          className={`h-7 text-sm pr-8 ${isValid ? 'border-green-500 bg-green-50' : ''}`}
          data-testid="place-search-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {searching && <Loader2 className="w-5 h-5 animate-spin text-green-500" />}
          {isValid && !searching && <Check className="w-4 h-4 text-green-500" />}
          {!searching && !isValid && <ChevronDown className="w-3 h-3 text-gray-400" />}
        </div>
      </div>
      
      {isValid && (
        <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {value.town} (Code: {value.pcode})
        </div>
      )}
      
      {/* No results message */}
      {noResults && !searching && inputValue.length >= 2 && (
        <div className="text-xs text-amber-600 mt-1 space-y-1">
          <p>No suburbs found for "{inputValue}".</p>
          <p className="text-gray-500">This field searches South African suburbs only. Try: SA city (e.g. Johannesburg), SA postal code (e.g. 7405), or suburb name (e.g. Maitland)</p>
        </div>
      )}
      
      {showDropdown && suggestions.length > 0 && (
        <div
          data-testid="place-suggestions-dropdown"
          data-dropdown-position={dropdownPosition}
          className={`absolute z-50 top-0 w-72 bg-white border border-gray-300 rounded shadow-xl max-h-64 overflow-y-auto ${
            dropdownPosition === "left" ? "right-full mr-2" : "left-full ml-2"
          }`}
        >
          <div className="sticky top-0 bg-gray-50 px-3 py-1.5 border-b border-gray-200 text-xs text-gray-500 font-medium">
            Select suburb
          </div>
          {suggestions.map((place, idx) => (
            <button
              key={`${place.place}-${idx}`}
              type="button"
              onClick={() => handleSelect(place)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 flex items-center justify-between border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{place.town}</span>
              <span className="text-xs text-gray-400">{place.pcode}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Product Search Dropdown for adding items from package presets
const ProductSearchDropdown = ({ packages, onSelect, placeholder = "Search product/SKU to add..." }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);

    if (query.length >= 1 && packages?.length > 0) {
      const filtered = packages.filter(pkg => 
        pkg.name?.toLowerCase().includes(query.toLowerCase()) ||
        pkg.sku?.toLowerCase().includes(query.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10);
      setSuggestions(filtered);
      setShowDropdown(true);
    } else if (query.length === 0 && packages?.length > 0) {
      // Show all packages when input is empty and focused
      setSuggestions(packages.slice(0, 10));
      setShowDropdown(true);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleFocus = () => {
    // Don't auto-search on focus — only show dropdown if user has already typed
    if (inputValue.length > 0 && suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleSelect = (pkg) => {
    onSelect?.(pkg);
    setInputValue("");
    setShowDropdown(false);
    toast.success(`Added ${pkg.name}`);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="h-7 text-sm pl-7 pr-2"
          data-testid="product-search-input"
        />
      </div>
      
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 top-full mt-1 w-80 bg-white border border-gray-300 rounded shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((pkg, idx) => (
            <button
              key={`${pkg.id}-${idx}`}
              type="button"
              onClick={() => handleSelect(pkg)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
            >
              {pkg.image_url ? (
                <img src={pkg.image_url} alt="" className="w-8 h-8 object-contain rounded bg-white" />
              ) : (
                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                  <Package className="w-4 h-4 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{pkg.name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  {pkg.sku && <span className="font-mono">{pkg.sku}</span>}
                  {pkg.weight && <span>{pkg.weight}kg</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {showDropdown && inputValue.length > 0 && suggestions.length === 0 && (
        <div className="absolute z-50 left-0 top-full mt-1 w-80 bg-white border border-gray-300 rounded shadow-lg p-4 text-center text-gray-500 text-sm">
          No products found
        </div>
      )}
    </div>
  );
};

// Customer search from previous waybills
const CustomerSearchInput = ({ value, onChange, onCustomerSelect, placeholder, "data-testid": dataTestId = "from-company" }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (value !== undefined && value !== inputValue) setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange?.(val);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (val.length >= 2) {
      setSearching(true);
      timeoutRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/waybills/customers/search?q=${encodeURIComponent(val)}`);
          if (res.data?.customers?.length > 0) {
            setSuggestions(res.data.customers);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        } catch { setSuggestions([]); }
        setSearching(false);
      }, 400);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (customer) => {
    setInputValue(customer.name);
    setShowDropdown(false);
    setSuggestions([]);
    onCustomerSelect?.(customer);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          className="h-7 text-sm"
          data-testid={dataTestId}
        />
        {searching && (
          <Loader2 className="absolute right-2 top-1.5 w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 top-full mt-1 w-full min-w-[300px] bg-white border border-gray-300 rounded shadow-lg max-h-56 overflow-y-auto">
          <div className="sticky top-0 bg-gray-50 px-3 py-1 border-b text-[10px] text-gray-500 font-medium">
            Previous customers ({suggestions.length})
          </div>
          {suggestions.map((c, i) => (
            <button
              key={c.name ? `${c.name}-${c.postal_code || i}` : `suggestion-${i}`}
              onClick={() => handleSelect(c)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <div className="text-xs font-medium text-gray-900">{c.name}</div>
              <div className="text-[10px] text-gray-500">
                {[c.city, c.postal_code, c.phone].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const CreateWaybillPage = () => {
  const { orderId: paramOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = paramOrderId || searchParams.get('order_id');
  const navigate = useNavigate();
  const location = useLocation();
  const { packages, fetchPackages } = useAppContext();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  const [createdWaybill, setCreatedWaybill] = useState(null);
  const [quotePreview, setQuotePreview] = useState(null); // Quote preview before waybill creation
  // Tracks whether staff explicitly picked a service from the dropdown. Once true, their choice
  // is the master and the LOC/ECO auto-select never overrides it.
  const [serviceManuallySet, setServiceManuallySet] = useState(false);
  const [confirmingQuote, setConfirmingQuote] = useState(false);
  const [showSANotice, setShowSANotice] = useState(true); // SA-only popup
  const [attemptedSubmit, setAttemptedSubmit] = useState(false); // Reveals red validation blocks after a failed submit

  // Determine current section from URL
  // Determine section type
  // - /quotes/* = Outbound shipment (warehouse sends TO customer) - Verify on RIGHT (receiver)
  // - /collections/* or /returns/* = Return (customer sends TO warehouse) - Verify on LEFT (sender)
  const isQuotesSection = location.pathname.includes('/quotes');
  const isCollectionsSection = location.pathname.includes('/collections') || location.pathname.includes('/returns');
  const currentSection = isQuotesSection ? 'quotes' : isCollectionsSection ? 'collections' : 'quotes';

  // Form state matching First Freight format
  const [formData, setFormData] = useState({
    // Header
    customer: "",
    account: "",
    date: new Date().toISOString().split('T')[0],
    costing: "N/A",
    // FROM (Sender)
    from_company: "",
    from_place: null,  // Now stores {place: ID, town: string, pcode: string}
    from_postal_code: "",
    from_address1: "",
    from_address2: "",
    from_address3: "",
    from_address4: "",
    from_contact: "",
    from_telephone1: "",
    from_telephone2: "",
    from_cell: "",
    from_email: "",
    from_notify: false,  // Default OFF for sender
    // TO (Recipient)
    to_company: "",
    to_place: null,  // Now stores {place: ID, town: string, pcode: string}
    to_postal_code: "",
    to_address1: "",
    to_address2: "",
    to_address3: "",
    to_address4: "",
    to_contact: "",
    to_telephone1: "",
    to_telephone2: "",
    to_cell: "",
    to_email: "",
    to_notify: true,  // Default ON for recipient
    // Reference & Services
    reference: "",
    service_type: "ECO",  // Default Economy
    additional_services: "N/A",
    insurance: false,
    declared_value: "",
    // Contents
    contents: []
  });

  useEffect(() => {
    fetchData();
    fetchPackages();
  }, [orderId]);

  // Service (LOC vs ECO) is determined from the live First Freight quote in handleSubmit:
  // FF only returns a LOC (Local Deliveries) rate for genuinely local routes, so a LOC rate
  // being present => local => LOC, otherwise ECO. No fragile hardcoded postcode heuristics.


  // Helper function to match order line items to package presets by SKU or name
  const matchItemsToPackages = (lineItems, packagesData) => {
    return (lineItems || []).map((item, index) => {
      // Try to find a matching package by SKU first, then by name
      const itemSku = item.sku || "";
      const itemName = item.name || "";
      
      let matchedPackage = null;
      if (itemSku && packagesData?.length > 0) {
        matchedPackage = packagesData.find(pkg => pkg.sku === itemSku);
      }
      if (!matchedPackage && itemName && packagesData?.length > 0) {
        // Try exact name match first
        matchedPackage = packagesData.find(pkg => pkg.name === itemName);
        // Then try partial match
        if (!matchedPackage) {
          matchedPackage = packagesData.find(pkg => 
            itemName.toLowerCase().includes(pkg.name?.toLowerCase()) ||
            pkg.name?.toLowerCase().includes(itemName.toLowerCase())
          );
        }
      }
      
      return {
        id: index + 1,
        pieces: item.quantity || 1,
        description: item.name || 'Item',
        length: matchedPackage?.length || "",
        width: matchedPackage?.width || "",
        height: matchedPackage?.height || "",
        mass: matchedPackage?.weight || "",
        packageId: matchedPackage?.id || null,  // Link to package for saving dimensions
        packageSku: matchedPackage?.sku || item.sku || null
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch settings and packages first
      const [settingsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/packages`)
      ]);
      const settingsData = settingsRes.data;
      const packagesData = packagesRes.data || [];
      setSettings(settingsData);

      // Wire Settings "Default Service": new waybills start on the chosen service (not always ECO).
      // A non-ECO default is a deliberate choice, so treat it as a manual pick -> the route-based
      // auto LOC/ECO rule won't override it (the backend still falls back to ECO if a service like
      // LOC isn't available on the route).
      const defService = (settingsData?.default_service || "ECO").toUpperCase();
      setFormData(prev => ({ ...prev, service_type: defService }));
      if (defService !== "ECO") setServiceManuallySet(true);

      // Check if order was passed via navigation state (from live search)
      const liveSearchOrder = location.state?.order;
      const fromLiveSearch = location.state?.fromLiveSearch;
      
      // If we have an orderId or live search order, fetch/use the order and pre-populate
      if (orderId || liveSearchOrder) {
        let orderData;
        
        if (fromLiveSearch && liveSearchOrder) {
          // Use order data from live search (already formatted)
          orderData = liveSearchOrder;
        } else if (orderId) {
          // Fetch from local database
          const orderRes = await axios.get(`${API}/orders/${orderId}`);
          orderData = orderRes.data;
        }
        
        if (orderData) {
          setOrder(orderData);
          
          // Pre-populate form from order
          const shipping = orderData.shipping_address || {};
          
          // Check if this is a return/collection (warehouse receives)
          const isReturn = location.pathname.includes('/collections');
          
          // Match order items to package presets for dimension tracking
          const matchedContents = matchItemsToPackages(orderData.line_items, packagesData);
        
        if (isReturn) {
          // RETURN: Customer sends FROM left, Warehouse receives TO right
          setFormData(prev => ({
            ...prev,
            customer: settingsData.company_name || "",
            account: settingsData.ff_account || "",
            // FROM (Customer sending back) - from order
            from_company: orderData.customer_name || "",
            from_place: { town: "", place: null, pcode: "" },  // Leave blank for user to search FF suburb
            from_postal_code: shipping.postal_code || "",
            from_address1: shipping.address1 || "",
            from_address2: shipping.address2 || "",
            from_address3: shipping.city || "",
            from_address4: shipping.province || "",
            from_contact: orderData.customer_name || "",
            from_telephone1: orderData.customer_phone || "",
            from_telephone2: "",
            from_cell: orderData.customer_phone || "",
            from_email: orderData.customer_email || "",
            // TO (Warehouse receiving) - from settings (already verified)
            to_company: settingsData.company_name || "",
            to_place: { town: settingsData.warehouse_name || settingsData.city || "", place: settingsData.place_id || null, pcode: settingsData.postal_code || "" },
            to_postal_code: settingsData.postal_code || "",
            to_address1: settingsData.address_line1 || "",
            to_address2: settingsData.address_line2 || "",
            to_address3: settingsData.city || "",
            to_address4: "",
            to_contact: settingsData.contact_person || "",
            to_telephone1: settingsData.phone || "",
            to_telephone2: "",
            to_cell: "",
            to_email: settingsData.email || "",
            // Reference
            reference: `RET-${orderData.order_number}`,
            // Contents from order items - matched to package presets for dimension saving
            contents: matchedContents
          }));
          
          // Auto-verify the FROM (customer) place from postal code
          if (shipping.postal_code || shipping.city) {
            setTimeout(() => {
              // Do NOT auto-verify — user must manually search and confirm the suburb
              // autoVerifyPlace(shipping.postal_code, shipping.city, true);
            }, 500);
          }
        } else {
          // OUTBOUND: Warehouse sends FROM left, Customer receives TO right
          setFormData(prev => ({
            ...prev,
            customer: settingsData.company_name || "",
            account: settingsData.ff_account || "",
            // FROM (Warehouse sending) - from settings (already verified)
            from_company: settingsData.company_name || "",
            from_place: { town: settingsData.warehouse_name || settingsData.city || "", place: settingsData.place_id || null, pcode: settingsData.postal_code || "" },
            from_postal_code: settingsData.postal_code || "",
            from_address1: settingsData.address_line1 || "",
            from_address2: settingsData.address_line2 || "",
            from_address3: settingsData.city || "",
            from_address4: "",
            from_contact: settingsData.contact_person || "",
            from_telephone1: settingsData.phone || "",
            from_telephone2: "",
            from_cell: "",
            from_email: settingsData.email || "",
            // TO (Customer receiving) - from order (place needs verification)
            to_company: orderData.customer_name || "",
            to_place: { town: "", place: null, pcode: "" },  // Leave blank for user to search FF suburb
            to_postal_code: shipping.postal_code || "",
            to_address1: shipping.address1 || "",
            to_address2: shipping.address2 || "",
            to_address3: shipping.city || "",
            to_address4: shipping.province || "",
            to_contact: orderData.customer_name || "",
            to_telephone1: orderData.customer_phone || "",
            to_telephone2: "",
            to_cell: orderData.customer_phone || "",
            to_email: orderData.customer_email || "",
            // Reference
            reference: `FF-${orderData.order_number}`,
            // Contents from order items - matched to package presets for dimension saving
            contents: matchedContents
          }));
          
          // Auto-verify the destination place from postal code
          if (shipping.postal_code || shipping.city) {
            setTimeout(() => {
              // Do NOT auto-verify — user must manually search and confirm the suburb
              // autoVerifyPlace(shipping.postal_code, shipping.city, false);
            }, 500);
          }
        }
        }
      } else {
        // No order - just populate from settings (Request Quote mode)
        // Check if this is a return/collection
        const isReturn = location.pathname.includes('/collections');
        
        if (isReturn) {
          // RETURN: Left side empty for customer, Right side has warehouse (receiving)
          setFormData(prev => ({
            ...prev,
            customer: settingsData.company_name || "",
            account: settingsData.ff_account || "",
            // FROM (Customer sending back) - empty for manual entry
            from_company: "",
            from_place: { town: "", place: null, pcode: "" },  // User must verify
            from_postal_code: "",
            from_address1: "",
            from_address2: "",
            from_address3: "",
            from_address4: "",
            from_contact: "",
            from_telephone1: "",
            from_telephone2: "",
            from_cell: "",
            from_email: "",
            // TO (Warehouse receiving) - from settings (already verified)
            to_company: settingsData.company_name || "",
            to_place: { town: settingsData.warehouse_name || settingsData.city || "", place: settingsData.place_id || null, pcode: settingsData.postal_code || "" },
            to_postal_code: settingsData.postal_code || "",
            to_address1: settingsData.address_line1 || "",
            to_address2: settingsData.address_line2 || "",
            to_address3: settingsData.city || "",
            to_address4: "",
            to_contact: settingsData.contact_person || "",
            to_telephone1: settingsData.phone || "",
            to_telephone2: "",
            to_cell: "",
            to_email: settingsData.email || "",
            // Reference
            reference: `RET-${Date.now()}`,
            // Contents - empty
            contents: []
          }));
        } else {
          // OUTBOUND Quote: Warehouse sends FROM left, empty TO for manual entry
          setFormData(prev => ({
            ...prev,
            customer: settingsData.company_name || "",
            account: settingsData.ff_account || "",
            // FROM (Warehouse sending) - from settings (already verified)
            from_company: settingsData.company_name || "",
            from_place: { town: settingsData.warehouse_name || settingsData.city || "", place: settingsData.place_id || null, pcode: settingsData.postal_code || "" },
            from_postal_code: settingsData.postal_code || "",
            from_address1: settingsData.address_line1 || "",
            from_address2: settingsData.address_line2 || "",
            from_address3: settingsData.city || "",
            from_address4: "",
            from_contact: settingsData.contact_person || "",
            from_telephone1: settingsData.phone || "",
            from_telephone2: "",
            from_cell: "",
            from_email: settingsData.email || "",
            // TO (Recipient) - empty for manual entry, needs verification
            to_company: "",
            to_place: { town: "", place: null, pcode: "" },
            to_postal_code: "",
            to_address1: "",
            to_address2: "",
            to_address3: "",
            to_address4: "",
            to_contact: "",
            to_telephone1: "",
            to_telephone2: "",
            to_cell: "",
            to_email: "",
            // Reference - auto-generate
            reference: `FF-${Date.now()}`,
            // Contents - empty
            contents: []
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (orderId) {
        toast.error("Failed to load order data");
        navigate("/orders");
      } else {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup place by postal code and try to verify
  const autoVerifyPlace = async (postalCode, city, isFrom = false) => {
    if (!postalCode && !city) return null;
    const query = postalCode || city;
    
    try {
      const response = await axios.get(`${API}/ff/places/verify/${encodeURIComponent(query)}`, { timeout: 15000 });
      if (response.data?.success && response.data?.places?.length > 0) {
        const places = response.data.places;
        
        // If only one result, auto-select it
        if (places.length === 1) {
          const place = places[0];
          if (isFrom) {
            setFormData(prev => ({ ...prev, from_place: place, from_postal_code: place.pcode }));
          } else {
            setFormData(prev => ({ ...prev, to_place: place, to_postal_code: place.pcode }));
          }
          return place;
        }
        
        // Multiple results - try to match by city name
        if (city) {
          const cityUpper = city.toUpperCase();
          const match = places.find(p => 
            p.town.toUpperCase().includes(cityUpper) || 
            cityUpper.includes(p.town.split(',')[0].toUpperCase())
          );
          if (match) {
            if (isFrom) {
              setFormData(prev => ({ ...prev, from_place: match, from_postal_code: match.pcode }));
            } else {
              setFormData(prev => ({ ...prev, to_place: match, to_postal_code: match.pcode }));
            }
            return match;
          }
        }
        
        // Couldn't auto-select - populate town field for manual selection
        if (isFrom) {
          setFormData(prev => ({ ...prev, from_place: { town: city || postalCode, place: null, pcode: postalCode || "" } }));
        } else {
          setFormData(prev => ({ ...prev, to_place: { town: city || postalCode, place: null, pcode: postalCode || "" } }));
        }
        return null;
      }
      
      // No match found
      if (isFrom) {
        setFormData(prev => ({ ...prev, from_place: { town: city || "", place: null, pcode: postalCode || "" } }));
      } else {
        setFormData(prev => ({ ...prev, to_place: { town: city || "", place: null, pcode: postalCode || "" } }));
      }
      return null;
    } catch (error) {
      console.error("Auto-verify error:", error);
      return null;
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateContent = (index, field, value) => {
    setFormData(prev => {
      const contents = [...prev.contents];
      contents[index] = { ...contents[index], [field]: value };
      return { ...prev, contents };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      contents: [...prev.contents, {
        id: prev.contents.length + 1,
        pieces: 1,
        description: "",
        length: "",
        width: "",
        height: "",
        mass: ""
      }]
    }));
  };

  const addFromPackagePreset = (pkg) => {
    setFormData(prev => ({
      ...prev,
      contents: [...prev.contents, {
        id: prev.contents.length + 1,
        pieces: 1,
        description: pkg.name,
        length: pkg.length || "",
        width: pkg.width || "",
        height: pkg.height || "",
        mass: pkg.weight || "",
        packageId: pkg.id,  // Track which package this came from
        packageSku: pkg.sku  // Track SKU for reference
      }]
    }));
    toast.success(`Added ${pkg.name}`);
  };

  // Save dimensions back to package preset when user enters them
  // If no packageId exists, create a new package preset
  const saveDimensionsToPackage = async (packageId, packageName, dimensions) => {
    // Only save if at least one dimension or weight has a value
    const hasValue = dimensions.length || dimensions.width || dimensions.height || dimensions.mass;
    if (!hasValue) return;
    
    try {
      if (packageId) {
        // Update existing package
        await axios.put(`${API}/packages/${packageId}`, {
          length: dimensions.length ? parseFloat(dimensions.length) : null,
          width: dimensions.width ? parseFloat(dimensions.width) : null,
          height: dimensions.height ? parseFloat(dimensions.height) : null,
          weight: dimensions.mass ? parseFloat(dimensions.mass) : null
        });
        toast.success(`Saved dimensions for "${packageName}" to Package Presets`);
      } else {
        // Create new package preset
        const response = await axios.post(`${API}/packages`, {
          name: packageName,
          sku: "",
          description: packageName,
          length: dimensions.length ? parseFloat(dimensions.length) : null,
          width: dimensions.width ? parseFloat(dimensions.width) : null,
          height: dimensions.height ? parseFloat(dimensions.height) : null,
          weight: dimensions.mass ? parseFloat(dimensions.mass) : null
        });
        toast.success(`Created new Package Preset: "${packageName}"`);
        // Return the new package ID so we can link it
        return response.data?.id;
      }
      // Refresh packages in context so other parts of the app have updated data
      fetchPackages();
    } catch (error) {
      console.error("Failed to save dimensions to package:", error);
      toast.error("Failed to save dimensions to package preset");
    }
    return null;
  };

  // Update content and optionally save dimensions to package
  const updateContentWithSave = (index, field, value) => {
    const item = formData.contents[index];
    
    // Update the form data
    updateContent(index, field, value);
    
    // If dimensions were updated, save them (create new package if needed)
    if (['length', 'width', 'height', 'mass'].includes(field) && item?.description) {
      // Debounce the save - wait 2 seconds after user stops typing
      if (window.dimensionSaveTimeout) {
        clearTimeout(window.dimensionSaveTimeout);
      }
      window.dimensionSaveTimeout = setTimeout(async () => {
        const updatedItem = { ...item, [field]: value };
        const newPackageId = await saveDimensionsToPackage(item.packageId, item.description, {
          length: updatedItem.length,
          width: updatedItem.width,
          height: updatedItem.height,
          mass: updatedItem.mass
        });
        
        // If a new package was created, link it to this item
        if (newPackageId && !item.packageId) {
          setFormData(prev => {
            const contents = [...prev.contents];
            contents[index] = { ...contents[index], packageId: newPackageId };
            return { ...prev, contents };
          });
        }
      }, 2000);
    }
  };

  const removeItem = () => {
    if (formData.contents.length === 0) return;
    setFormData(prev => ({
      ...prev,
      contents: prev.contents.slice(0, -1)
    }));
  };

  const removeItemAtIndex = (index) => {
    setFormData(prev => ({
      ...prev,
      contents: prev.contents.filter((_, i) => i !== index)
    }));
  };

  const clearAllItems = () => {
    setFormData(prev => ({
      ...prev,
      contents: []
    }));
  };

  const handleSubmit = async () => {
    setAttemptedSubmit(true);

    // Validation
    if (!formData.from_company || !formData.to_company) {
      toast.error("Please fill in sender and recipient details");
      return;
    }

    // Validate place verification
    if (!formData.from_place?.place) {
      toast.error("Sender's suburb/place is not verified — see the red box below the Place field");
      return;
    }

    if (!formData.to_place?.place) {
      toast.error("Recipient's suburb/place is not verified — see the red box below the Place field");
      return;
    }

    if (formData.contents.length === 0) {
      toast.error("Please add at least one content item");
      return;
    }

    // Insurance guard: never let staff insure for nothing
    if (formData.insurance && !(parseFloat(formData.declared_value) > 0)) {
      toast.error("Enter a declared value greater than 0 to insure this shipment, or untick Insurance.");
      return;
    }

    setSubmitting(true);

    // Calculate totals for quote preview
    const totalPieces = formData.contents.reduce((sum, c) => sum + (parseInt(c.pieces) || 1), 0);
    const totalWeight = formData.contents.reduce((sum, c) => sum + (parseFloat(c.mass) || 0) * (parseInt(c.pieces) || 1), 0);
    const totalVolume = formData.contents.reduce((sum, c) => {
      const l = parseFloat(c.length) || 0;
      const w = parseFloat(c.width) || 0;
      const h = parseFloat(c.height) || 0;
      const pieces = parseInt(c.pieces) || 1;
      return sum + (l * w * h * pieces / 1000000); // Convert to cubic meters
    }, 0);

    // Call First Freight API for actual pricing
    let quoteRates = [];
    let quoteno = null;
    let effectiveService = formData.service_type;
    try {
      const quotePayload = {
        origpers: formData.from_company,
        origpercontact: formData.from_contact || formData.from_company,
        origperadd1: formData.from_address1 || "Address",
        origperphone: formData.from_telephone1 || "",
        origpercell: formData.from_cellphone || formData.from_telephone1 || "",
        origplace: formData.from_place?.place || null,
        origtown: formData.from_place?.town || "",
        origperpcode: formData.from_postal_code || "",
        destpers: formData.to_company,
        destpercontact: formData.to_contact || formData.to_company,
        destperadd1: formData.to_address1 || "Address",
        destperphone: formData.to_telephone1 || "",
        destpercell: formData.to_cellphone || formData.to_telephone1 || "",
        destplace: formData.to_place?.place || null,
        desttown: formData.to_place?.town || "",
        destperpcode: formData.to_postal_code || "",
        service: formData.service_type,
        insurance: formData.insurance,
        declared_value: formData.declared_value,
        contents: formData.contents.map((c, i) => ({
          item: i + 1,
          pieces: parseInt(c.pieces) || 1,
          description: c.description || "Package",
          dim1: parseFloat(c.length) || 10,
          dim2: parseFloat(c.width) || 10,
          dim3: parseFloat(c.height) || 10,
          actmass: parseFloat(c.mass) || 1
        }))
      };

      const quoteResponse = await axios.post(`${API}/ff/get-quote`, quotePayload, { timeout: 30000 });
      if (quoteResponse.data && quoteResponse.data.length > 0) {
        quoteno = quoteResponse.data[0].quoteno;
        quoteRates = quoteResponse.data[0].rates || [];

        // Service selection:
        // • DEFAULT (no manual pick): auto-select LOC for local routes (FF only returns a LOC
        //   rate when the route is genuinely local) or ECO otherwise.
        // • MANUAL pick: whatever the user chose in the Service dropdown is the master — never
        //   override it; just warn if that service has no rate for this route.
        const hasLoc = quoteRates.some(r => (r.service || "").toUpperCase() === "LOC");
        const autoService = hasLoc ? "LOC" : "ECO";
        if (serviceManuallySet) {
          effectiveService = formData.service_type;
          const available = quoteRates.some(
            (r) => (r.service || "").toUpperCase() === effectiveService.toUpperCase()
          );
          if (!available) {
            toast.warning(`${effectiveService} isn't available for this route — choose another service.`);
          }
        } else {
          effectiveService = autoService;
          if (autoService !== formData.service_type) {
            setFormData((prev) => ({ ...prev, service_type: autoService }));
          }
          toast.info(hasLoc
            ? "Local delivery — auto-selected Local (LOC)"
            : "Long-distance delivery — auto-selected Economy (ECO)");
        }
      }
    } catch (quoteError) {
      console.warn("Quote API call failed, showing preview without pricing:", quoteError);
      const errorDetail = quoteError.response?.data?.detail || "Could not fetch live pricing";
      toast.error(errorDetail);
      setSubmitting(false);
      return; // Don't show preview without valid quote - let user fix the issue
    }

    // Show quote preview with pricing
    setQuotePreview({
      from: {
        company: formData.from_company,
        place: formData.from_place?.town || '',
        address: [formData.from_address1, formData.from_address2, formData.from_address3].filter(Boolean).join(', '),
        contact: formData.from_contact,
        phone: formData.from_telephone1
      },
      to: {
        company: formData.to_company,
        place: formData.to_place?.town || '',
        address: [formData.to_address1, formData.to_address2, formData.to_address3].filter(Boolean).join(', '),
        contact: formData.to_contact,
        phone: formData.to_telephone1,
        email: formData.to_email
      },
      service: {
        ECO: 'Economy (ECO)',
        LOC: 'Local (LOC)',
        LSD: 'Local Sameday (LSD)',
        NAF: 'Air Freight (NAF)',
        ONX: 'Overnight Express (ONX)',
      }[effectiveService] || effectiveService,
      reference: formData.reference,
      contents: formData.contents,
      totals: {
        pieces: totalPieces,
        weight: totalWeight.toFixed(2),
        volume: totalVolume.toFixed(3)
      },
      insurance: formData.insurance,
      declared_value: formData.declared_value,
      quoteno: quoteno,
      rates: quoteRates
    });
    
    setSubmitting(false);
  };

  const confirmAndCreateWaybill = async () => {
    setConfirmingQuote(true);
    try {
      const totalWeight = formData.contents.reduce((sum, c) => sum + (parseFloat(c.mass) || 0) * (parseInt(c.pieces) || 1), 0);
      
      const payload = {
        order_id: orderId || `manual-${Date.now()}`,
        shopify_order_id: order?.shopify_order_id || null,
        sender_company: formData.from_company,
        sender_address1: formData.from_address1,
        sender_address2: formData.from_address2,
        sender_address3: formData.from_address3,
        sender_address4: formData.from_address4,
        sender_postal_code: formData.from_place?.pcode || formData.from_postal_code || "",
        sender_city: formData.from_place?.town || "",
        sender_place_id: formData.from_place?.place || null,
        sender_phone: formData.from_telephone1,
        sender_contact: formData.from_contact,
        recipient_name: formData.to_company,
        recipient_address1: formData.to_address1,
        recipient_address2: formData.to_address2,
        recipient_address3: formData.to_address3,
        recipient_address4: formData.to_address4,
        recipient_postal_code: formData.to_place?.pcode || formData.to_postal_code || "",
        recipient_city: formData.to_place?.town || "",
        recipient_place_id: formData.to_place?.place || null,
        recipient_phone: formData.to_telephone1,
        recipient_email: formData.to_email,
        service_type: formData.service_type,
        special_instructions: "",
        reference: formData.reference,
        num_parcels: formData.contents.reduce((sum, c) => sum + (parseInt(c.pieces) || 1), 0),
        total_weight: totalWeight,
        contents: formData.contents.map((c, i) => ({
          item: i + 1,
          description: c.description,
          pieces: parseInt(c.pieces) || 1,
          dim1: parseFloat(c.length) || 1,
          dim2: parseFloat(c.width) || 1,
          dim3: parseFloat(c.height) || 1,
          actmass: parseFloat(c.mass) || 1
        })),
        quote_no: quotePreview?.quoteno || null,  // Pass existing quote number to avoid re-requesting
        insurance: formData.insurance,
        declared_value: formData.declared_value
      };

      const response = await axios.post(`${API}/waybills`, payload);
      setCreatedWaybill(response.data);
      setQuotePreview(null);
      toast.success("Waybill created successfully!");
    } catch (error) {
      console.error("Error creating waybill:", error);
      toast.error(error.response?.data?.detail || "Failed to create waybill");
    } finally {
      setConfirmingQuote(false);
    }
  };

  const cancelQuotePreview = () => {
    setQuotePreview(null);
  };

  // Save quote for later (without creating waybill)
  const handleSaveQuote = async () => {
    // Basic validation
    if (!formData.from_company || !formData.to_company) {
      toast.error("Please fill in sender and recipient company names");
      return;
    }

    setSavingQuote(true);
    try {
      const quoteData = {
        from_company: formData.from_company,
        from_contact: formData.from_contact,
        from_address1: formData.from_address1,
        from_address2: formData.from_address2,
        from_phone: formData.from_telephone,
        from_cell: formData.from_cell,
        from_place: formData.from_place,
        from_postal_code: formData.from_postal_code,
        to_company: formData.to_company,
        to_contact: formData.to_contact,
        to_address1: formData.to_address1,
        to_address2: formData.to_address2,
        to_phone: formData.to_telephone,
        to_cell: formData.to_cell,
        to_email: formData.to_email,
        to_place: formData.to_place,
        to_postal_code: formData.to_postal_code,
        service: formData.service,
        reference: formData.reference,
        contents: formData.contents.map(c => ({
          pieces: parseInt(c.pieces) || 1,
          description: c.description || "",
          dim1: parseFloat(c.length) || 0,
          dim2: parseFloat(c.width) || 0,
          dim3: parseFloat(c.height) || 0,
          actmass: parseFloat(c.mass) || 0
        })),
        quote_number: quotePreview?.quoteno || "",
        rates: quotePreview?.rates || []
      };

      await axios.post(`${API}/quotes`, quoteData);
      toast.success("Quote saved successfully!");
      navigate("/quotes/view");
    } catch (error) {
      console.error("Error saving quote:", error);
      toast.error("Failed to save quote");
    } finally {
      setSavingQuote(false);
    }
  };

  const handleExit = () => {
    navigate("/orders");
  };

  const downloadPDF = async () => {
    if (!createdWaybill) return;
    try {
      const response = await axios.get(`${API}/waybills/${createdWaybill.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waybill-${createdWaybill.waybill_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("A4 Waybill PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const downloadTextWaybill = async () => {
    if (!createdWaybill) return;
    try {
      const response = await axios.get(`${API}/waybills/${createdWaybill.id}/text-waybill`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `blank-waybill-${createdWaybill.waybill_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Blank waybill downloaded");
    } catch (error) {
      toast.error("Failed to download blank waybill");
    }
  };

  const downloadLabels = async () => {
    if (!createdWaybill) return;
    try {
      const response = await axios.get(`${API}/waybills/${createdWaybill.id}/labels`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `labels-${createdWaybill.waybill_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Parcel stickers downloaded");
    } catch (error) {
      toast.error("No parcel stickers available for this waybill");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F0F0F0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
      </div>
    );
  }

  // Calculate quote details for modal
  const selectedRate = quotePreview?.rates?.find(r => r.service === formData.service_type);
  const baseTotal = selectedRate ? (parseFloat(selectedRate.total) || 0) : 0;
  const insuranceAmount = quotePreview?.insurance && quotePreview?.declared_value && selectedRate
    ? parseFloat(selectedRate.insurance || 0) : 0;
  const grandTotal = baseTotal; // FF 'total' already includes the insurance premium — don't double-add
  
  // Check if we're on pure quotes page (NOT from an order)
  const urlSearchParams = new URLSearchParams(location.search);
  const isFromOrder = urlSearchParams.has('order_id');
  const isQuotePage = location.pathname === '/quotes/request' && !isFromOrder;

  // Quote Summary Modal - plain JSX (NOT a component to avoid unmount/remount flash)
  const quoteSummaryModal = (
    <Dialog open={!!quotePreview} onOpenChange={(open) => !open && cancelQuotePreview()}>
      <DialogContent className="max-w-[850px] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="bg-[#16a34a] px-5 py-3 flex items-center justify-between">
          <DialogTitle className="font-semibold text-white text-lg m-0">Quote Summary</DialogTitle>
          <div className="flex items-center gap-3">
            <span className="text-white/80 text-sm">Ref: <span className="font-mono font-bold text-white">{quotePreview?.reference || '-'}</span></span>
            <ServiceBadge service={formData.service_type} testId="quote-preview-service-badge" />
            {selectedRate?.duedate && (
              <div className="bg-white/20 rounded px-2 py-1 text-white text-sm">ETA: {selectedRate.duedate}</div>
            )}
          </div>
        </div>
        
        {/* Content - Square Card Layout */}
        <div className="p-5">
          {/* From / To Row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* From */}
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-3 h-3 text-blue-600" />
                </div>
                <span className="font-bold text-gray-700 text-xs uppercase">From</span>
              </div>
              <p className="font-semibold text-gray-900 text-sm">{quotePreview?.from?.company}</p>
              <p className="text-gray-600 text-xs mt-0.5">{quotePreview?.from?.place}</p>
              <p className="text-gray-500 text-xs truncate">{quotePreview?.from?.address}</p>
              <p className="text-gray-500 text-xs mt-1">Tel: {quotePreview?.from?.phone}</p>
            </div>

            {/* To */}
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-3 h-3 text-green-600" />
                </div>
                <span className="font-bold text-gray-700 text-xs uppercase">To</span>
              </div>
              <p className="font-semibold text-gray-900 text-sm">{quotePreview?.to?.company}</p>
              <p className="text-gray-600 text-xs mt-0.5">{quotePreview?.to?.place}</p>
              <p className="text-gray-500 text-xs truncate">{quotePreview?.to?.address}</p>
              <p className="text-gray-500 text-xs mt-1">Tel: {quotePreview?.to?.phone}</p>
            </div>
          </div>

          {/* Contents & Pricing Row */}
          <div className="grid grid-cols-[1fr_200px] gap-4">
            {/* Contents Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <span className="font-bold text-gray-700 text-xs">Contents</span>
                <div className="flex gap-3 text-xs">
                  <span><span className="font-bold">{quotePreview?.totals?.pieces}</span> pcs</span>
                  <span><span className="font-bold">{quotePreview?.totals?.weight}</span> kg</span>
                  <span><span className="font-bold">{quotePreview?.totals?.volume}</span> m³</span>
                </div>
              </div>
              <div className="max-h-[120px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-1.5 font-medium text-gray-600">Description</th>
                      <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-12">Qty</th>
                      <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-24">L×W×H</th>
                      <th className="text-right px-3 py-1.5 font-medium text-gray-600 w-16">Mass</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotePreview?.contents?.map((item, idx) => (
                      <tr key={item.description ? `${item.description}-${idx}` : `quote-item-${idx}`} className="border-b border-gray-100">
                        <td className="px-3 py-1.5 text-gray-800 truncate max-w-[180px]">{item.description || '-'}</td>
                        <td className="text-center px-2 py-1.5 text-gray-600">{item.pieces}</td>
                        <td className="text-center px-2 py-1.5 text-gray-500">{item.length}×{item.width}×{item.height}</td>
                        <td className="text-right px-3 py-1.5 text-gray-600">{item.mass} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="border border-green-200 rounded-lg overflow-hidden bg-green-50/30">
              {selectedRate ? (
                <>
                  <div className="bg-green-100 px-3 py-2 border-b border-green-200 flex items-center justify-between">
                    <span className="font-bold text-green-800 text-xs">{selectedRate.name}</span>
                    <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded font-mono">{selectedRate.service}</span>
                  </div>
                  <div className="p-3 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cartage</span>
                      <span className="font-medium">R {parseFloat(selectedRate.cartage || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(selectedRate.outly || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Outlying</span>
                        <span className="font-medium">R {parseFloat(selectedRate.outly).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surcharges</span>
                      <span className="font-medium">R {parseFloat(selectedRate.totsurcharge || 0).toFixed(2)}</span>
                    </div>
                    {insuranceAmount > 0 && (
                      <div className="flex justify-between text-blue-700">
                        <span>Insurance</span>
                        <span className="font-medium">R {insuranceAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">VAT (15%)</span>
                      <span className="font-medium">R {parseFloat(selectedRate.vat || 0).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-green-300 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-700">Total</span>
                        <span className="text-xl font-bold text-green-700">R {grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    {quotePreview?.quoteno && (
                      <p className="text-xs text-green-600 font-mono text-center mt-1">Quote #{quotePreview.quoteno}</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-4 h-full flex items-center justify-center">
                  <p className="text-xs text-amber-700 text-center">Pricing unavailable<br/>Waybill can still be created</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-200">
            <Button
              variant="outline"
              size="sm"
              onClick={cancelQuotePreview}
              disabled={confirmingQuote}
            >
              Back to Edit
            </Button>
            
            {isQuotePage && (
              <Button 
                variant="outline"
                size="sm"
                onClick={handleSaveQuote}
                disabled={savingQuote}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                {savingQuote ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Quote
              </Button>
            )}
            
            <Button
              onClick={confirmAndCreateWaybill}
              disabled={confirmingQuote}
              size="sm"
              className="flex-1 bg-[#16a34a] hover:bg-[#15803d] text-white"
              data-testid="confirm-waybill-btn"
            >
              {confirmingQuote ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Waybill...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" />Confirm & Create Waybill</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Success state
  if (createdWaybill) {
    const fulfillmentLink = `https://firstfreight.pperfect.com/?w=${createdWaybill.waybill_number}`;
    
    return (
      <div className="min-h-screen bg-[#F0F0F0] p-4">
        <div className="max-w-lg mx-auto mt-10 bg-white border border-gray-300 shadow-lg">
          <div className="bg-[#E8E8E8] px-4 py-2 border-b border-gray-300 flex items-center justify-between">
            <span className="font-semibold text-gray-700">Waybill Created</span>
            <button onClick={() => navigate("/orders")} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Waybill Created Successfully</h2>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <p className="text-xs text-gray-500 mb-1">Waybill Number</p>
              <p className="text-xl font-mono font-bold text-[#16a34a]">{createdWaybill.waybill_number}</p>
              <p className="text-xs text-gray-500 mt-2">Reference: {createdWaybill.reference}</p>
            </div>

            {/* Shopify Fulfillment Status */}
            {createdWaybill.shopify_fulfillment ? (
              createdWaybill.shopify_fulfillment.success ? (
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-900 text-sm">Shopify Order Fulfilled</p>
                      <p className="text-xs text-green-700 mt-1">
                        Tracking number <code className="bg-green-100 px-1 rounded font-mono">{createdWaybill.waybill_number}</code> has been added to Shopify and the customer has been notified.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900 text-sm">Shopify Fulfillment Pending</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Auto-fulfillment could not be completed: {createdWaybill.shopify_fulfillment.error || 'Unknown error'}
                      </p>
                      <p className="text-xs text-amber-600 mt-2">
                        You can retry or manually fulfill this order in Shopify Admin with tracking number: <code className="bg-amber-100 px-1 rounded font-mono">{createdWaybill.waybill_number}</code>
                      </p>
                      <Button 
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/waybills/${createdWaybill.id}/fulfill-shopify`);
                            if (res.data.success) {
                              setCreatedWaybill(prev => ({...prev, shopify_fulfillment: res.data}));
                              toast.success("Shopify order fulfilled!");
                            } else {
                              toast.error(res.data.error || "Fulfillment failed");
                            }
                          } catch (err) {
                            toast.error("Failed to fulfill on Shopify");
                          }
                        }}
                        size="sm" 
                        variant="outline" 
                        className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                        data-testid="retry-fulfill-btn"
                      >
                        Retry Shopify Fulfillment
                      </Button>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-900 text-sm">Manual Fulfillment</p>
                    <p className="text-xs text-blue-700 mt-1">
                      This waybill was not linked to a Shopify order. You can add tracking manually in Shopify Admin with number: <code className="bg-blue-100 px-1 rounded font-mono">{createdWaybill.waybill_number}</code>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-2 flex-wrap">
              <Button onClick={downloadPDF} size="sm" className="bg-[#4A7C4E] hover:bg-[#3D6840] text-white" data-testid="download-pdf-btn">
                <Download className="w-4 h-4 mr-1" />
                Normal A4
              </Button>
              <Button onClick={downloadLabels} size="sm" className="bg-[#16a34a] hover:bg-[#D4551A] text-white" data-testid="download-labels-btn">
                <Tag className="w-4 h-4 mr-1" />
                Parcel Stickers
              </Button>
              <Button 
                onClick={() => {
                  navigator.clipboard.writeText(fulfillmentLink);
                  toast.success("Tracking link copied!");
                }} 
                variant="outline" 
                size="sm"
              >
                Copy Tracking Link
              </Button>
              <Button onClick={() => navigate("/orders")} variant="outline" size="sm">
                Back to Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F0] pb-[200px]">
      {/* Quote Summary Modal */}
      {quoteSummaryModal}
      
      {/* Main Form */}
      <div className="p-4">
        <div className="bg-white border border-gray-300 shadow-sm max-w-5xl mx-auto">
          {/* Form Header */}
          <div className="bg-[#E8E8E8] px-4 py-2 border-b border-gray-300 flex items-center justify-between">
            <span className="font-semibold text-gray-700">Request New Quote</span>
            <button onClick={handleExit} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4">
            {/* SA-only notice modal */}
            {showSANotice && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-md mx-4 overflow-hidden">
                  <div className="bg-red-600 px-6 py-4">
                    <h3 className="text-white font-bold text-base flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.07 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                      South African Courier Service
                    </h3>
                  </div>
                  <div className="px-6 py-5 space-y-3">
                    <p className="text-sm text-gray-700">
                      EasyWaybill integrates with <strong>First Freight</strong>, a South African domestic courier. Suburb verification and shipping quotes work with <strong>South African addresses only</strong>.
                    </p>
                    <p className="text-sm text-gray-700">
                      International postal codes will not return results — this is expected.
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Test with these SA codes:</p>
                      <ul className="text-xs text-gray-600 space-y-0.5">
                        <li><strong>7405</strong> — Maitland, Cape Town</li>
                        <li><strong>2001</strong> — Johannesburg CBD</li>
                        <li><strong>4001</strong> — Durban</li>
                        <li><strong>Greyton</strong> — Western Cape</li>
                      </ul>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end border-t">
                    <button
                      onClick={() => { setShowSANotice(false); handleExit(); }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid="sa-notice-reject"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => setShowSANotice(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      data-testid="sa-notice-accept"
                    >
                      I Understand, Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Customer, Account & Date Row */}
            <div className="grid grid-cols-3 gap-6 mb-4">
              <FormRow label="Customer:">
                <Input 
                  value={formData.customer} 
                  onChange={(e) => updateFormData("customer", e.target.value)}
                  className="h-7 text-sm bg-gray-50"
                  disabled
                />
              </FormRow>
              <FormRow label="Account:">
                <Input 
                  value={formData.account} 
                  onChange={(e) => updateFormData("account", e.target.value)}
                  className="h-7 text-sm bg-gray-50"
                  disabled
                />
              </FormRow>
              <FormRow label="Date:">
                <Input 
                  type="date"
                  value={formData.date} 
                  onChange={(e) => updateFormData("date", e.target.value)}
                  className="h-7 text-sm"
                />
              </FormRow>
            </div>

            {/* FROM / TO Section */}
            <div className="grid grid-cols-2 gap-8 border border-gray-200 rounded p-4 mb-4">
              {/* FROM Column */}
              <div className="space-y-2">
                <FormRow label="From:">
                  {isCollectionsSection ? (
                    <CustomerSearchInput
                      value={formData.from_company}
                      onChange={(val) => updateFormData("from_company", val)}
                      onCustomerSelect={(customer) => {
                        updateFormData("from_company", customer.name);
                        updateFormData("from_address1", customer.address1 || "");
                        updateFormData("from_address2", customer.address2 || "");
                        updateFormData("from_address3", customer.city || "");
                        updateFormData("from_address4", customer.province || "");
                        updateFormData("from_postal_code", customer.postal_code || "");
                        updateFormData("from_telephone1", customer.phone || "");
                        updateFormData("from_cell", customer.phone || "");
                        updateFormData("from_contact", customer.contact || customer.name);
                        updateFormData("from_email", customer.email || "");
                      }}
                      placeholder="Search customer..."
                      data-testid="from-company"
                    />
                  ) : (
                    <Input 
                      value={formData.from_company} 
                      onChange={(e) => updateFormData("from_company", e.target.value)}
                      className="h-7 text-sm"
                      data-testid="from-company"
                    />
                  )}
                </FormRow>
                <FormRow 
                  label={formData.from_place?.place ? "Confirmed:" : "Verify:"} 
                  labelClassName={formData.from_place?.place ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}
                >
                  <div
                    data-testid="from-place-block"
                    className={`rounded-md ${attemptedSubmit && !formData.from_place?.place ? "border border-red-400 bg-red-50 p-2" : ""}`}
                  >
                    <PlaceSearchDropdown
                      value={formData.from_place}
                      onPlaceSelect={(place) => {
                        updateFormData("from_place", place);
                        updateFormData("from_postal_code", place.pcode);
                      }}
                      placeholder="Search SA suburb or postal code (e.g. 7405)..."
                      postalCode=""
                      cityHint=""
                    />
                    {attemptedSubmit && !formData.from_place?.place && (
                      <div data-testid="from-place-error" className="mt-2 flex items-start gap-1.5 text-xs text-red-700">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Sender suburb isn't verified — the waybill can't be created yet. Type the suburb name or postal code above and pick the matching result from the dropdown. This usually comes from your default sender address in <strong>Settings → Shipping</strong>.
                        </span>
                      </div>
                    )}
                  </div>
                </FormRow>
                <FormRow label="Postal Code:">
                  <Input 
                    value={formData.from_postal_code} 
                    onChange={(e) => updateFormData("from_postal_code", e.target.value)}
                    className="h-7 text-sm bg-gray-50"
                    placeholder="Auto-filled from Place"
                    readOnly
                  />
                </FormRow>
                <FormRow label="Address:">
                  <div className="space-y-1">
                    <Input 
                      value={formData.from_address1} 
                      onChange={(e) => updateFormData("from_address1", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 1"
                    />
                    <Input 
                      value={formData.from_address2} 
                      onChange={(e) => updateFormData("from_address2", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 2"
                    />
                    <Input 
                      value={formData.from_address3} 
                      onChange={(e) => updateFormData("from_address3", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="City"
                    />
                    <Input 
                      value={formData.from_address4} 
                      onChange={(e) => updateFormData("from_address4", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Province"
                    />
                  </div>
                </FormRow>
                <FormRow label="Contact:">
                  <Input 
                    value={formData.from_contact} 
                    onChange={(e) => updateFormData("from_contact", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Telephone:">
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      value={formData.from_telephone1} 
                      onChange={(e) => updateFormData("from_telephone1", e.target.value)}
                      className="h-7 text-sm"
                    />
                    <Input 
                      value={formData.from_telephone2} 
                      onChange={(e) => updateFormData("from_telephone2", e.target.value)}
                      className="h-7 text-sm"
                    />
                  </div>
                </FormRow>
                <FormRow label="Cell:">
                  <Input 
                    value={formData.from_cell} 
                    onChange={(e) => updateFormData("from_cell", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Email:">
                  <div className="flex items-center gap-2">
                    <Input 
                      value={formData.from_email} 
                      onChange={(e) => updateFormData("from_email", e.target.value)}
                      className="h-7 text-sm flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <Checkbox 
                        checked={formData.from_notify} 
                        onCheckedChange={(v) => updateFormData("from_notify", v)}
                        className="w-4 h-4"
                      />
                      Notify Contact
                    </label>
                  </div>
                </FormRow>
              </div>

              {/* TO Column */}
              <div className="space-y-2">
                <FormRow label="To:">
                  {!isCollectionsSection ? (
                    <CustomerSearchInput
                      value={formData.to_company}
                      onChange={(val) => updateFormData("to_company", val)}
                      onCustomerSelect={(customer) => {
                        updateFormData("to_company", customer.name);
                        updateFormData("to_address1", customer.address1 || "");
                        updateFormData("to_address2", customer.address2 || "");
                        updateFormData("to_address3", customer.city || "");
                        updateFormData("to_address4", customer.province || "");
                        updateFormData("to_postal_code", customer.postal_code || "");
                        updateFormData("to_telephone1", customer.phone || "");
                        updateFormData("to_cell", customer.phone || "");
                        updateFormData("to_contact", customer.contact || customer.name);
                        updateFormData("to_email", customer.email || "");
                      }}
                      placeholder="Search customer..."
                      data-testid="to-company"
                    />
                  ) : (
                    <Input 
                      value={formData.to_company} 
                      onChange={(e) => updateFormData("to_company", e.target.value)}
                      className="h-7 text-sm"
                      data-testid="to-company"
                    />
                  )}
                </FormRow>
                <FormRow 
                  label={formData.to_place?.place ? "Confirmed:" : "Verify:"} 
                  labelClassName={formData.to_place?.place ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}
                >
                  <div
                    data-testid="to-place-block"
                    className={`rounded-md ${attemptedSubmit && !formData.to_place?.place ? "border border-red-400 bg-red-50 p-2" : ""}`}
                  >
                    <PlaceSearchDropdown
                      value={formData.to_place}
                      onPlaceSelect={(place) => {
                        updateFormData("to_place", place);
                        updateFormData("to_postal_code", place.pcode);
                      }}
                      placeholder="Search SA suburb or postal code (e.g. 7405)..."
                      dropdownPosition="left"
                      postalCode=""
                      cityHint=""
                    />
                    {attemptedSubmit && !formData.to_place?.place && (
                      <div data-testid="to-place-error" className="mt-2 flex items-start gap-1.5 text-xs text-red-700">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Recipient suburb isn't verified — the waybill can't be created yet. Type the suburb name or postal code above and pick the matching result from the dropdown.
                        </span>
                      </div>
                    )}
                  </div>
                </FormRow>
                <FormRow label="Address:">
                  <div className="space-y-1">
                    <Input 
                      value={formData.to_address1} 
                      onChange={(e) => updateFormData("to_address1", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 1"
                    />
                    <Input 
                      value={formData.to_address2} 
                      onChange={(e) => updateFormData("to_address2", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 2"
                    />
                    <Input 
                      value={formData.to_address3} 
                      onChange={(e) => updateFormData("to_address3", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="City"
                    />
                    <Input 
                      value={formData.to_address4} 
                      onChange={(e) => updateFormData("to_address4", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Province"
                    />
                  </div>
                </FormRow>
                <FormRow label="Contact:">
                  <Input 
                    value={formData.to_contact} 
                    onChange={(e) => updateFormData("to_contact", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Telephone:">
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      value={formData.to_telephone1} 
                      onChange={(e) => updateFormData("to_telephone1", e.target.value)}
                      className="h-7 text-sm"
                      data-testid="to-telephone1"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 whitespace-nowrap">Code:</span>
                      <Input 
                        value={formData.to_postal_code} 
                        className="h-7 text-sm bg-gray-50 flex-1"
                        readOnly
                        data-testid="to-postal-code-display"
                      />
                    </div>
                  </div>
                </FormRow>
                <FormRow label="Cell:">
                  <Input 
                    value={formData.to_cell} 
                    onChange={(e) => updateFormData("to_cell", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Email:">
                  <div className="flex items-center gap-2">
                    <Input 
                      value={formData.to_email} 
                      onChange={(e) => updateFormData("to_email", e.target.value)}
                      className="h-7 text-sm flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <Checkbox 
                        checked={formData.to_notify} 
                        onCheckedChange={(v) => updateFormData("to_notify", v)}
                        className="w-4 h-4"
                      />
                      Notify Contact
                    </label>
                  </div>
                </FormRow>
              </div>
            </div>

            {/* Reference & Services Row */}
            <div className="flex items-center flex-wrap gap-4 border border-gray-200 rounded p-3 mb-4">
              <FormRowInline label="Reference:">
                <Input 
                  value={formData.reference} 
                  onChange={(e) => updateFormData("reference", e.target.value)}
                  className="h-7 text-sm w-32"
                />
              </FormRowInline>
              <FormRowInline label="Service:">
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => { setServiceManuallySet(true); updateFormData("service_type", v); }}
                  data-testid="service-type-select"
                >
                  <SelectTrigger className="h-7 text-sm w-40" data-testid="service-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ECO" data-testid="service-eco">Economy (ECO)</SelectItem>
                    <SelectItem value="LOC" data-testid="service-loc">Local (LOC)</SelectItem>
                    <SelectItem value="LSD" data-testid="service-lsd">Local Sameday (LSD)</SelectItem>
                    <SelectItem value="NAF" data-testid="service-naf">Air Freight (NAF)</SelectItem>
                    <SelectItem value="ONX" data-testid="service-onx">Overnight Express (ONX)</SelectItem>
                  </SelectContent>
                </Select>
              </FormRowInline>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <span>Insurance:</span>
                <Checkbox 
                  checked={formData.insurance} 
                  onCheckedChange={(v) => updateFormData("insurance", v)}
                  className="w-4 h-4"
                  data-testid="insurance-checkbox"
                />
              </label>
              <FormRowInline label="Declared Value:">
                <div className="flex items-center gap-2">
                  <Input 
                    value={formData.declared_value} 
                    onChange={(e) => updateFormData("declared_value", e.target.value)}
                    className={`h-7 text-sm w-24 bg-gray-50 ${formData.insurance && !(parseFloat(formData.declared_value) > 0) ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                    placeholder="N/A"
                    disabled={!formData.insurance}
                    data-testid="declared-value-input"
                  />
                  {formData.insurance && !(parseFloat(formData.declared_value) > 0) && (
                    <span className="text-xs text-red-600 whitespace-nowrap" data-testid="declared-value-warning">
                      Enter a value to insure
                    </span>
                  )}
                </div>
              </FormRowInline>
            </div>

            {/* Product Search - Always visible */}
            <div className="border border-gray-200 rounded-t p-3 bg-blue-50">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Add Product:</span>
                <div className="flex-1 max-w-md">
                  <ProductSearchDropdown 
                    packages={packages}
                    onSelect={addFromPackagePreset}
                    placeholder="Type product name or SKU to search..."
                  />
                </div>
                <span className="text-xs text-gray-500">or manually add below</span>
              </div>
            </div>

            {/* Contents Table */}
            <div className="border border-t-0 border-gray-200 rounded-b mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-8"></th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-16">Pieces</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">Description</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Length (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Width (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Height (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-28">Mass (Total kg)</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.contents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <Package className="w-8 h-8 text-gray-300" />
                          <span>No items added yet. Use the search above or click "Add Item" below.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    formData.contents.map((item, index) => (
                      <tr key={item.description ? `${item.description}-${index}` : `item-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-1 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeItemAtIndex(index)}
                            className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Remove this item"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            min="1"
                            value={item.pieces} 
                            onChange={(e) => updateContent(index, "pieces", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            value={item.description} 
                            onChange={(e) => updateContent(index, "description", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.length} 
                            onChange={(e) => updateContentWithSave(index, "length", e.target.value)}
                            className="h-7 text-sm w-full"
                            placeholder={item.packageId ? "Enter L" : ""}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.width} 
                            onChange={(e) => updateContentWithSave(index, "width", e.target.value)}
                            className="h-7 text-sm w-full"
                            placeholder={item.packageId ? "Enter W" : ""}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.height} 
                            onChange={(e) => updateContentWithSave(index, "height", e.target.value)}
                            className="h-7 text-sm w-full"
                            placeholder={item.packageId ? "Enter H" : ""}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            step="0.1"
                            value={item.mass} 
                            onChange={(e) => updateContentWithSave(index, "mass", e.target.value)}
                            className="h-7 text-sm w-full"
                            placeholder={item.packageId ? "Enter kg" : ""}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Content Actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addItem}
                  className="h-7 text-xs"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-3 h-3 mr-1 text-green-600" />
                  Add Item
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearAllItems}
                  className="h-7 text-xs"
                  disabled={formData.contents.length === 0}
                >
                  <Trash2 className="w-3 h-3 mr-1 text-red-600" />
                  Clear All Contents Items
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-2 pt-4 border-t border-gray-200">
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#4A7C4E] hover:bg-[#3D6840] text-white h-8 px-4 min-w-[120px]"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Getting Quote...
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Get Quote
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={handleExit}
                className="h-8 px-4"
              >
                <XSquare className="w-4 h-4 mr-1 text-gray-500" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const FormRow = ({ label, children, labelClassName = "" }) => (
  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
    <label className={`text-sm text-right pt-1 ${labelClassName || "text-gray-600"}`}>{label}</label>
    <div>{children}</div>
  </div>
);

const FormRowInline = ({ label, children }) => (
  <div className="flex items-center gap-2">
    <label className="text-sm text-gray-600">{label}</label>
    {children}
  </div>
);

export default CreateWaybillPage;
